import { pad } from 'js/mylib/Utils';
import moment from 'moment';
import { t } from 'i18next';
import { formatISO, lightFormat, startOfMonth } from 'date-fns';
import {
  DEFAULT_DATE_FORMATS,
  sanitizeMomentDateFormat,
  sanitizeMomentDateTimeFormat,
  sanitizeMomentTimeFormat,
} from './DateFormat';

type FormatType = 'date' | 'datetime' | 'time';
type DateInput = Date | number | string;
type SafeFormatCache = Partial<
  Record<
    FormatType,
    {
      raw: string;
      safe: string;
    }
  >
>;

declare global {
  interface Window {
    dateTimeFormats?: Partial<Record<FormatType, string>>;
  }
}

const SAFE_FORMAT_CACHE: SafeFormatCache = {};

function getSafeFormat(formatType: FormatType): string {
  const rawFormat: string =
    window.dateTimeFormats?.[formatType] ??
    t(`format.${formatType}`, DEFAULT_DATE_FORMATS[formatType]);

  const cached = SAFE_FORMAT_CACHE[formatType];
  if (cached && cached.raw === rawFormat) {
    return cached.safe;
  }

  let safeFormat;

  if (formatType === 'date') {
    safeFormat = sanitizeMomentDateFormat(rawFormat);
  } else if (formatType === 'time') {
    safeFormat = sanitizeMomentTimeFormat(rawFormat);
  } else {
    safeFormat = sanitizeMomentDateTimeFormat(rawFormat);
  }

  SAFE_FORMAT_CACHE[formatType] = {
    raw: rawFormat,
    safe: safeFormat,
  };

  return safeFormat;
}

function toDate(date: DateInput): Date {
  return date instanceof Date ? date : new Date(date);
}

export function dateTimeToLocaleString(
  date: DateInput | null | undefined
): string {
  if (date) {
    return moment(toDate(date)).format(getSafeFormat('datetime'));
  }
  return '';
}

export function dateToLocaleString(date: DateInput | null | undefined): string {
  if (date) {
    return moment(toDate(date)).format(getSafeFormat('date'));
  }
  return '';
}

export function timeToLocaleString(date: DateInput | null | undefined): string {
  if (date) {
    return moment(toDate(date)).format(getSafeFormat('time'));
  }
  return '';
}

export function date_diff_indays(dt1: Date, dt2: Date): number {
  return Math.floor(
    (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
      Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
      (1000 * 60 * 60 * 24)
  );
}

export function time_to_hour_minute(time_str: string): {
  h: number;
  m: number;
} {
  return {
    h: parseInt(time_str.split(':')[0]),
    m: parseInt(time_str.split(':')[1]),
  };
}

export function datetimeToLocalIso(
  datetime: DateInput | null | undefined
): string {
  //YYYY-MM-DDThh:mmTZD
  if (datetime) {
    let d = toDate(datetime);
    return (
      String(d.getFullYear()) +
      '-' +
      String(pad(d.getMonth() + 1, 2)) +
      '-' +
      String(pad(d.getDate(), 2)) +
      'T' +
      String(pad(d.getHours(), 2)) +
      ':' +
      String(pad(d.getMinutes(), 2))
    );
  }

  return '';
}

export function formatISODate(datetime: Date | number): string {
  return formatISO(datetime, { representation: 'date' });
}

/** Current time in string format suitable for log messages */
export function logTime(): string {
  return lightFormat(new Date(), 'HH:mm:ss.SSS');
}

export function statsDefaultDateRange(): {
  start_date: string;
  end_date: string;
} {
  const now = new Date();
  return {
    start_date: formatISODate(startOfMonth(now)),
    end_date: formatISODate(now),
  };
}

const DAY_MILLISECONDS = 86400000;
const MS_DAY_OFFSET = 25569;

/**
 * JS Date to OLE Automation date
 * @param {date} date
 * @returns {number}
 */
export function toOADate(date: Date): number {
  const timezoneOffset = date.getTimezoneOffset() / (60 * 24);
  return date.getTime() / DAY_MILLISECONDS + (MS_DAY_OFFSET - timezoneOffset);
}
